import { gql } from 'graphql-request';
import { graphqlClient } from '../client';
import { Charge } from '@/components/company/types/planTypes';
import { updateChargeInRedux } from '@/store/actions/chargeActions';
import { ChargeType } from '@/types/chargeTypes';

// The GraphQL mutation for updating a charge
const UPDATE_CHARGE_MUTATION = gql`
  mutation UpdateCharge($id: ID!, $input: ChargeUpdateInput!) {
    updateCharge(id: $id, input: $input) {
      id
      type
      currency
      price
      notes
      price_per
      included_quantity
      overage_price
      overage_quantity
      minimum_price
      pricing_metric {
        id
        display_name
        modality
      }
    }
  }
`;

// Interface for the charge update input
export interface ChargeUpdateInput {
  price?: number | null;
  currency?: string | null;
  price_per?: string | null;
  // per field doesn't exist in the charges table - only use price_per
  type?: ChargeType | null;
  pricing_metric_id?: string | null;
  notes?: string | null;
  included_quantity?: number | null;
  overage_price?: string | null;
  overage_quantity?: number | null;
  minimum_price?: string | null;
}

// Interface for the mutation response
export interface UpdateChargeResponse {
  updateCharge: Charge;
}

/**
 * Updates a charge using GraphQL mutation
 * 
 * @param id The ID of the charge to update
 * @param input The updated charge data
 * @returns The updated charge or throws an error
 */
export async function updateChargeMutation(
  id: string,
  input: ChargeUpdateInput
): Promise<UpdateChargeResponse> {
  try {
    console.log(`🔄 [GraphQL] Updating charge ${id} with:`, input);
    
    // Execute the GraphQL mutation
    const response = await graphqlClient.request<UpdateChargeResponse>(
      UPDATE_CHARGE_MUTATION,
      { id, input }
    );
    
    console.log('✅ [GraphQL] Charge updated successfully:', response);
    
    // Update Redux store if response contains the updated charge
    if (response && response.updateCharge) {
      // Note: We're handling this in EditChargeModal too, but adding it here ensures
      // the Redux store is updated regardless of which component calls this mutation
      try {
        updateChargeInRedux(response.updateCharge);
      } catch (reduxError) {
        // Don't let Redux update failures break the main flow
        console.error('⚠️ [Redux] Failed to update Redux store:', reduxError);
      }
    }
    
    return response;
  } catch (error) {
    console.error('❌ [GraphQL] Error updating charge:', error);
    throw error;
  }
}
